// 장면에 표시할 기록만 고른다. 진행 상태·능력치·게임 난수는 바꾸지 않는다.
import { eventRecords, lifePhase, sceneMilestones } from './life.js';
import { GROUP_LABEL } from './rules.js';
import { natalProps } from './natalProps.js';

const FIELD_KIND = { 비겁: 'community', 식상: 'studio', 재성: 'finance', 관성: 'office', 인성: 'study' };
const FIELD_COLOR = {
  비겁: ['#4e7868', '#edf3ed'], 식상: ['#a9684f', '#f8eee5'], 재성: ['#977339', '#f7f0df'],
  관성: ['#596c85', '#edf1f6'], 인성: ['#74658d', '#f1edf6'],
};
const JOB_KIND = {
  sales: 'office', counselor: 'community', pr: 'office', hotel: 'service', activist: 'community',
  writer: 'writing', chef: 'kitchen', designer: 'studio', musician: 'music', developer: 'code',
  owner: 'service', investor: 'finance', accountant: 'finance', trader: 'office', realtor: 'office',
  civil: 'office', police: 'public', lawyer: 'office', military: 'public', teacher: 'school',
  corp: 'office', researcher: 'research', doctor: 'medical', editor: 'writing', clergy: 'community',
};
const KIND_LABEL = {
  nursery: '놀이 소품', school: '배움의 책상', study: '학습 공간', research: '연구 도구',
  studio: '창작 작업대', kitchen: '조리 공간', code: '개발 작업대', music: '음악 작업실',
  writing: '글 쓰는 책상', finance: '검토의 책상', office: '업무 공간', service: '안내 공간',
  community: '함께하는 자리', garden: '생활 정원', medical: '의료 도구', public: '현장 도구',
  presentation: '발표 도구', schedule: '일정 관리 도구', budget: '생활비 검토 도구', meeting: '만남의 자리',
  collaboration: '공동 작업 도구', practice: '연습 도구', travel: '이동 준비 도구', review: '비교·검토 도구',
};

const JOB_PROPS = {
  sales: ['상담 보드', '명함'], counselor: ['상담 노트', '탁상시계'], pr: ['카메라', '홍보 보드'],
  hotel: ['열쇠 보관대', '안내 벨'], activist: ['안내 피켓', '활동 자료'], writer: ['원고', '펜'],
  chef: ['냄비', '도마'], designer: ['색상 견본', '스케치'], musician: ['악기', '악보'],
  developer: ['노트북', '코드 화면'], owner: ['상품 진열대', '계산대'], investor: ['검토 보드', '조건표'],
  accountant: ['장부', '계산기'], trader: ['지구본', '운송 상자'], realtor: ['건물 모형', '도면'],
  civil: ['도장', '서류 보관함'], police: ['안전모', '무전기'], corp: ['명찰', '업무 보드'],
  lawyer: ['저울', '법률 책'], military: ['지도', '쌍안경'], teacher: ['칠판', '교재'],
  researcher: ['현미경', '실험 도구'], doctor: ['청진기', '의료 가방'], editor: ['교정 원고', '책'],
  clergy: ['촛대', '열린 책'],
};
const AGE_PROPS = {
  infant: ['모빌', '딸랑이'], child: ['블록', '연', '공'], school: ['가방', '필통', '책상'],
  youth: ['배낭', '지도', '노트'], entry: ['일정판', '서류함'], middle: ['정리 서가', '공구', '탁상시계'],
  transition: ['기록 노트', '화분', '스탠드'], later: ['독서 의자', '앨범', '찻잔'],
};
const RELATION_PROPS = {
  멘토: ['안내등', '참고서'], 친구: ['돗자리', '보드게임'], 라이벌: ['체스', '타이머'],
  동료: ['공유 도구', '작업 자료'], 연인: ['짝 잔', '편지'], 후배: ['설명 노트', '연필'],
};
const ACTION_PROPS = {
  presentation: ['발표 보드', '마이크'], schedule: ['달력', '일정 노트'], budget: ['항목별 봉투', '계산기'],
  meeting: ['초대장', '원탁'], collaboration: ['공동 작업판', '도구'], practice: ['연습 도구', '작업 모형'],
  travel: ['지도', '가방'], review: ['비교 서류', '돋보기'],
};

function ageStage(age) {
  if (age < 3) return 'infant';
  if (age < 8) return 'child';
  if (age < 15) return 'school';
  if (age < 24) return 'youth';
  if (age < 36) return 'entry';
  if (age < 50) return 'middle';
  if (age < 60) return 'transition';
  return 'later';
}

function visualVariant(run, key) {
  let value = ((run.seed ?? 0) + run.age * 7919) >>> 0;
  for (const char of key) value = (Math.imul(value ^ char.charCodeAt(0), 16777619)) >>> 0;
  return value % 4;
}

function kindForActivity(record) {
  const field = record.field ?? record.targetField;
  if (record.source === 'focus') return FIELD_KIND[field] ?? 'study';
  const choice = record.choiceLabel ?? (record.text ?? '').split(' → ').slice(1).join(' → ');
  // 대안에서 하지 않기로 한 원래 행동이 아니라 실제 실행 문구를 그린다.
  const text = choice.split(/대신|말고|하지 않고|하지않고/).at(-1).trim();
  if (/일정|시간표|일정표|사용 순서|기한|순서를 정/.test(text)) return 'schedule';
  if (/생활비|예산|지출|저축|저금|구매처|발주|손실/.test(text)) return 'budget';
  if (/비교|검토|조건|견적|계약/.test(text)) return 'review';
  if (/유학|여행|출장|방문|다른 지역|이동/.test(text)) return 'travel';
  if (/요리|조리|주방|음식|식당|메뉴/.test(text)) return 'kitchen';
  if (/음악|악기|연주|작곡|노래/.test(text)) return 'music';
  if (/코드|프로그래|디버|소프트웨어|개발자/.test(text)) return 'code';
  if (/원고|집필|글쓰기|출판|편집|문장/.test(text)) return 'writing';
  if (/실험|연구|논문/.test(text)) return 'research';
  if (/진료|의료|환자/.test(text)) return 'medical';
  if (/발표|전시|공개|소개|시연|설명하는 결과물/.test(text)) return 'presentation';
  if (/연습|실습|작게.*시작|작은.*시작|고쳐|수정|다시 시도/.test(text)) return 'practice';
  if (/함께|공동|역할.*나누|분담|동료|인수인계/.test(text)) return 'collaboration';
  if (/모임|약속|만남|사람.*모|대화|의견|교류|협의/.test(text)) return 'meeting';
  if (/공부|학습|배우|입문|이론|자료.*읽|질문/.test(text)) return 'study';
  return FIELD_KIND[field] ?? 'studio';
}

function latestAction(run) {
  const events = eventRecords(run).filter((record) => record.choiceKind !== 'decline'
    && record.outcome !== 'skipped' && GROUP_LABEL[record.field ?? record.targetField]);
  const focused = (run.log ?? []).filter((record) => record.kind === 'focus' && GROUP_LABEL[record.field]);
  // 같은 해에는 구체 사건을 우선한다. 옛 집중 기록을 특정 활동 완료로 바꾸지 않는다.
  return [...events.map((record) => ({ ...record, source: 'event' })),
    ...focused.map((record) => ({ ...record, source: 'focus' }))]
    .sort((left, right) => right.age - left.age || (left.source === right.source ? 0 : left.source === 'event' ? -1 : 1))[0] ?? null;
}

export function scenePresentation(run) {
  const phase = lifePhase(run.age), views = [];
  const milestone = sceneMilestones(run);
  const latest = latestAction(run);
  const people = (run.people ?? []).filter((person) => person.status === 'near');
  const decorate = (view) => ({ ...view, variant: visualVariant(run, `${view.kind}-${view.jobId ?? view.stage ?? view.role ?? ''}`),
    palette: FIELD_COLOR[view.field] ?? ['#6a795a', '#eef1e8'], kindLabel: KIND_LABEL[view.kind],
    propLabels: view.propLabels ?? ACTION_PROPS[view.kind] ?? [KIND_LABEL[view.kind]] });

  if (latest) {
    const field = latest.field ?? latest.targetField;
    const outcome = { success: '완료 기록', failure: '시도 기록' }[latest.outcome] ?? '행동 기록';
    views.push(decorate({ id: 'recent', label: '최근 행동', field, kind: kindForActivity(latest), renderer: 'action',
      eyebrow: `${latest.age}세 · ${GROUP_LABEL[field]} · ${outcome}`,
      title: latest.source === 'focus' ? `${GROUP_LABEL[field]}에 쓴 시간` : latest.title ?? '기록한 활동',
      detail: latest.choiceLabel ?? latest.text ?? '실제로 남은 행동 기록을 표시합니다.',
      noteLabel: latest.source === 'focus' ? '집중 기록' : outcome,
      noteTitle: latest.source === 'focus' ? `${GROUP_LABEL[field]} 행동` : latest.choiceLabel ?? latest.title,
      noteDetail: latest.outcome === 'failure' ? '완료 성과로 집계하지 않은 시도입니다.' : `${latest.age}세의 기록`,
      accent: 'stationery' }));
  }

  if (run.job) {
    const job = run.job, title = job.level <= 3 ? job.levels?.[job.level - 1] : job.branch?.name;
    views.push(decorate({ id: 'career', label: '직업', field: job.field,
      renderer: 'career', jobId: job.id, level: job.level, propLabels: JOB_PROPS[job.id] ?? ['서류', '업무 도구'],
      kind: JOB_KIND[job.id] ?? FIELD_KIND[job.field] ?? 'office',
      eyebrow: '직업의 장면', title: title ?? job.name,
      detail: `${job.name} · ${job.level}단계`, noteLabel: '직업 기록', noteTitle: job.name,
      noteDetail: `${job.level}단계 · ${GROUP_LABEL[job.field] ?? job.field}`, accent: 'stationery' }));
  }

  // 원국에서 나온 소품(일간 오행의 소품, 현재 막의 소품). 새 규칙의 진행에만 보인다.
  const natal = run.luckModel === 'environment' ? natalProps(run) : null;
  if (natal) {
    const labels = [natal.element.label, natal.act?.label].filter(Boolean);
    views.push(decorate({ id: 'natal', label: '원국 소품', field: '인성', kind: 'garden', renderer: 'natal',
      propLabels: labels, eyebrow: `원국의 소품 · ${natal.element.el} 기운`, title: labels.join(' · '),
      detail: natal.act ? `${natal.act.act}막 소품과 일간 오행의 소품` : '일간 오행의 소품',
      noteLabel: '원국의 소품', noteTitle: natal.element.label,
      noteDetail: natal.act ? `${natal.act.label} · 게임 규칙으로 고른 소품` : '게임 규칙으로 고른 소품', accent: 'plants' }));
  }

  if (people.length) {
    const memories = people.flatMap((person) => (person.memories ?? []).map((memory) => ({ ...memory, person })))
      .sort((left, right) => right.age - left.age);
    const pending = people.filter((person) => person.promise?.status === 'pending')
      .sort((left, right) => left.promise.dueAge - right.promise.dueAge);
    const person = pending[0] ?? memories[0]?.person ?? people[run.age % people.length];
    const latestMemory = [...(person.memories ?? [])].sort((left, right) => right.age - left.age)[0];
    const promise = person.promise;
    const state = { pending: 'promised', kept: 'kept', broken: 'broken' }[promise?.status] ?? 'none';
    const stateLabels = { promised: '약속 달력', kept: '약속 기록', broken: '일정 조정 메모' };
    const roleProps = RELATION_PROPS[person.role] ?? ['원탁', '찻잔'];
    const metrics = [Number.isFinite(person.trust) ? `신뢰 ${person.trust}` : null,
      Number.isFinite(person.closeness) ? `친밀도 ${person.closeness}` : null].filter(Boolean).join(' · ');
    views.push(decorate({ id: 'people', label: '관계', field: '비겁', kind: 'community',
      renderer: 'relationship', role: person.role, state,
      propLabels: [...roleProps, ...(stateLabels[state] ? [stateLabels[state]] : [])],
      eyebrow: `관계의 장면 · 곁의 사람 ${people.length}명`, title: `${person.name} · ${person.role}`,
      detail: latestMemory ? `${latestMemory.age}세 · ${latestMemory.text ?? latestMemory.title}` : metrics,
      noteLabel: state === 'promised' ? '남아 있는 약속' : '인물의 기록',
      noteTitle: state === 'promised' ? `${promise.dueAge}세 · ${GROUP_LABEL[promise.field] ?? promise.field} 약속` : `${person.name} · ${person.role}`,
      noteDetail: metrics || '공동 활동·거절·약속은 실제 기록대로 남습니다.', accent: 'cups' }));
  }

  const stage = ageStage(run.age);
  const kind = ['infant', 'child'].includes(stage) ? 'nursery' : ['school', 'youth'].includes(stage) ? 'school' : 'garden';
  views.push(decorate({ id: 'life', label: run.age < 15 ? '성장기' : '생활', kind,
    renderer: 'age', stage, propLabels: AGE_PROPS[stage],
    eyebrow: `${run.age}세 · ${phase.label}`, title: phase.question,
    detail: run.age < 15 ? '나이에 맞춘 생활 장면입니다.' : '출생 풍경 위에 현재 기록을 더합니다.',
    noteLabel: '현재 나이', noteTitle: `${run.age}세`, noteDetail: phase.label, accent: 'plants' }));

  return { views, milestone, phase };
}
